'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { Bell, MessageSquareWarning, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';

/**
 * 顶栏通知铃铛（Next.js / React 端）
 * - 每 30 秒轮询一次 unread-count
 * - 数字 ≥ 1 时显示红点 + 数字 badge
 * - hover 时弹出下拉菜单（含"提交反馈"+"查看全部通知"）
 * - 点击铃铛本体 → 跳到通知列表（同标签）
 * - 点击"提交反馈" → 新开标签到 /dongniai/u/feedback/new（"提交与反馈"页）
 * - 点击"查看全部通知" → 同标签跳通知列表
 */
const POLL_INTERVAL_MS = 30 * 1000;
const FEEDBACK_NEW_URL = '/dongniai/u/feedback/new';   // "提交与反馈"页
const NOTICE_LIST_URL  = '/u/notice/list';              // 通知列表

export default function NoticeBell() {
  const router = useRouter();
  const [unread, setUnread] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await fetch('/dongniai/api/notification/unread-count', {
        credentials: 'include',
        cache: 'no-store',
      });
      const json = await res.json();
      if (json && (json.code === 1 || json.success)) {
        // 2026-07-14 P1 fix: API 实际返回 unread_count 在顶层
        // ({"success":true,"unread_count":2,...}), 老代码读 json.data.unread_count 永远=0
        // 跟 public/assets/js/notice-bell.js line 33-37 (2026-07-10 P0 修复) 一致
        // (my-list endpoint 才是真的 data 包裹, 这里不要混淆)
        const count = (json.data && json.data.unread_count) || json.unread_count || 0;
        setUnread(Number(count));
      }
    } catch {
      /* 静默失败，不影响 UI */
    }
  }, []);

  useEffect(() => {
    fetchUnread();
    timerRef.current = setInterval(fetchUnread, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchUnread]);

  // 页面可见性 — 切回前台时立即刷新
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnread();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [fetchUnread]);

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // 关闭时清掉 timer
  useEffect(() => {
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, []);

  // hover 200ms 延迟关闭（防抖）
  const handleMouseEnter = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setOpen(true);
  };
  const handleMouseLeave = () => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setOpen(false), 200);
  };

  return (
    <div
      ref={containerRef}
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button
        variant="ghost"
        size="icon"
        onClick={() => router.push(NOTICE_LIST_URL)}
        className="relative"
        aria-label="通知"
        title="通知"
      >
        <Bell className="size-5" />
        {unread > 0 && (
          <span
            // 【2026-06-29 老板报错】铃铛数字偏上，让它往下移一些 overlap 铃铛图标更多。
            // 原：-top-0.5 -right-0.5 （top=-2px，右上靠按钮边缘，偏上）
            // 新：top-1 -right-0.5   （top=+4px，进入图标内，与 20px 铃铛重叠多）
            className="absolute top-1 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold bg-destructive text-destructive-foreground rounded-full"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-[9999] min-w-[180px] bg-popover text-popover-foreground rounded-md border shadow-md py-1 animate-in fade-in-0 zoom-in-95"
        >
          {/* 提交反馈：新开标签 */}
          <a
            href={FEEDBACK_NEW_URL}
            target="_blank"
            rel="noopener"
            role="menuitem"
            className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer rounded-sm"
            onClick={() => setOpen(false)}
          >
            <MessageSquareWarning className="size-4 text-muted-foreground" />
            <span>提交反馈</span>
          </a>
          {/* 查看全部通知：同标签 */}
          <a
            href={NOTICE_LIST_URL}
            role="menuitem"
            className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer rounded-sm"
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
              router.push(NOTICE_LIST_URL);
            }}
          >
            <List className="size-4 text-muted-foreground" />
            <span>查看全部通知</span>
          </a>
        </div>
      )}
    </div>
  );
}
